import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Animated, ActivityIndicator } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale, notchTop, height } from '@components/frame';
import { navigateTo, isWeb } from '@helpers';

import AnimatedHeader from '@components/common/AnimatedHeader';
import Button from '@styles/buttons';
import ColorWrapper from '@styles/colors';
import Line from '@components/common/line';
import LineButton from '@components/common/lineButton';
import { getBoxShadowStyle } from '@styles/shadow';

import { searchInvitedUsers, inviteUser } from '@reducers/membership';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const InviteBtn = ({ inviteUser, user, user: { userProfile } }) => {
  const [loading, setLoading] = useState(false);
  return (
    <View style={{ marginRight: scale(-6) }}>
      <Button
        {...{
          variant: 'S',
          type: 'Secondary',
          loading,
          onPress: async () => {
            setLoading(true);
            await inviteUser({
              emailAddress: user.emailAddress,
              role: user.role,
              userProfile: {
                firstName: userProfile.firstName,
                lastName: userProfile.lastName,
              },
            });
            setLoading(false);
          },
        }}
      >
        {i18n.t('Re-Invite')}
      </Button>
    </View>
  );
};

const Users = ({ C, searchInvitedUsers, invitedUsers, inviteUser, invitedLoaded, isInvitedUsersMore }) => {
  const [alowLoadMore, setLoadMore] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scrollRef = useRef(null);

  const config = {
    filters: {
      status: {
        value: 'Invited',
        valueType: 'string',
      },
    },
    orderBy: {
      invitedOn: 'Desc',
    },
  };

  useEffect(() => {
    searchInvitedUsers({
      ...config,
      reset: true,
    });
  }, []);

  const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }) => {
    return layoutMeasurement.height + contentOffset.y >= contentSize.height - 1500;
  };

  const scrollListener = ({ nativeEvent }) => {
    if (alowLoadMore && isCloseToBottom(nativeEvent) && isInvitedUsersMore) {
      setLoadMore(false);
      searchInvitedUsers({
        ...config,
        callback: () => setLoadMore(true),
      });
    }
  };

  const invitedUsersFormatted = invitedUsers.map((u) => {
    const { userProfile = {}, role, emailAddress } = u;
    return {
      name: `${userProfile.firstName} ${userProfile.lastName}`,
      value: () => <InviteBtn {...{ user: u, inviteUser }} />,
      bottom: role,
      onClick: () => {
        navigateTo('Settings', {
          screen: 'UserDetails',
          params: {
            emailAddress,
          },
        });
      },
      valueFunction: true,
      noArrow: true,
    };
  });

  return [
    <View
      key="UsersList"
      style={{
        width: '100%',
        paddingBottom: scale(130),
        paddingTop: notchTop,
        backgroundColor: C('Background/Light/Detail'),
        height: '100%',
      }}
    >
      <AnimatedHeader
        {...{
          backText: i18n.t('Users'),
          text: i18n.t('Re-Invite'),
          C,
          animated: false,
          back: true,
          backgroundColor: 'Background/Light/Detail',
          borderBottomWidth: 0,
        }}
      />
      <Animated.ScrollView
        scrollEventThrottle={16}
        ref={scrollRef}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: fadeAnim } } }], {
          useNativeDriver: true,
          listener: scrollListener,
        })}
        style={{
          backgroundColor: C('Background / Light / Detail'),
          width: '100%',
          minHeight: height,
          paddingTop: scale(isWeb ? 76 : 83),
        }}
      >
        <View style={{ width: '100%', paddingLeft: scale(15), paddingRight: scale(15) }}>
          <View style={[getBoxShadowStyle({ type: 'Field', C }), { marginTop: scale(5), width: '100%', borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
            {invitedUsersFormatted.map((i, index) => (
              <Line
                {...{
                  item: i,
                  index,
                  array: invitedUsersFormatted,
                  bigSize: true,
                  C,
                  topBorder: index !== 0,
                  roundTop: index === 0,
                  roundBottom: index === invitedUsersFormatted.length - 1,
                }}
              />
            ))}
          </View>
        </View>
        {!invitedLoaded || !alowLoadMore ? (
          <View style={{ width: '100%', marginTop: scale(20), justifyContent: 'center', height: 50 }}>
            <ActivityIndicator size="small" color="#777777" />
          </View>
        ) : null}
        {invitedLoaded && (
          <View style={{ width: '100%', paddingLeft: scale(15), paddingRight: scale(15) }}>
            <View style={{ marginTop: scale(20) }}>
              <LineButton
                {...{
                  C,
                  onPress: () => {
                    if (deleting) {
                      return;
                    }
                  },
                  bg: modalFieldBg,
                  loading: deleting,
                  text: i18n.t('Clear Re-Invite List'),
                  color: 'Label/Light/Error',
                }}
              />
            </View>
          </View>
        )}
        <View style={{ width: '100%', minHeight: 200 }} />
      </Animated.ScrollView>
    </View>,
  ];
};

export default connect(
  ({ membership: { invitedUsers, isInvitedUsersMore, invitedLoaded } }) => ({ invitedUsers, isInvitedUsersMore, invitedLoaded }),
  {
    searchInvitedUsers,
    inviteUser,
  }
)(ColorWrapper(Users));
